import { useEffect, useMemo, useRef, useState } from 'react'

import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useMutation } from 'react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import BeatLoader from 'react-spinners/BeatLoader'

import {
    IconCalendarLinesWhite,
    IconPlus,
    IconShareCircles
} from 'assets/svg/sprite'
import { IconButton } from 'components/IconButton/IconButton'
import { addToast } from 'components/Toast/Toast'
import InlineDatePicker from 'components/UI/InlineDatePicker/InlineDatePicker'
import ScheduleContent from 'components/UI/Modal/ScheduleContent/ScheduleContent'
import { OptionsDropdown } from 'components-ref/molecules'
import { useModalContext } from 'context/modalContext'
import { usePageTitle } from 'context/pageTitleContext'
// import DesktopAdditionalContent from 'features/DesktopAdditionalContent/DesktopAdditionalContent'
import DropDownSelect from 'features/DropdownSelectNew/DropDownSelect'
import { AllIcons } from 'helpers/allIcons'
import { Icons } from 'helpers/icons'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import {
    deleteScheduledPostFn,
    getScheduledContent,
    postContentNow
} from 'services/endpoints/schedule'
import { useScheduleCacheStore } from 'store/useScheduleCacheStore'
import {
    type ISchedule,
    type IScheduledListDate,
    type postType
} from 'types/interfaces/ITypes'

import styles from './SchedulePage.module.scss'
import { ScheduleList } from '../ScheduleList'

export function SchedulePage() {
    const { setTitle } = usePageTitle()

    const [scheduledContent, setScheduledContent] = useState<ISchedule[]>([])
    const [search, setSearch] = useState<string>('')
    const [selectedOption, setSelectedOption] = useState<string>('')
    const [isOptionsNavOpen, setIsOptionsNavOpen] = useState<boolean>(false)
    const [showBigCalendar, setShowBigCalendar] = useState<boolean>(true)
    const [contentId, setContentId] = useState<string>('')
    const [scheduleId, setScheduleId] = useState<string>('')
    const [contentType, setContentType] = useState<string>('post')
    const [isLoading, setIsLoading] = useState<boolean>(true)

    const [date, setDate] = useState<Date | null>(null)

    const modalData = useModalContext()

    const { t } = useTranslation()
    const navigate = useNavigate()
    const location = useLocation()

    const hasMountedFilter = useRef(false)
    const fetchRequestId = useRef(0)

    // Cache store
    const { scheduled: cachedScheduled, setScheduled: setCache } =
        useScheduleCacheStore()

    const schedulesToCalendar = (data: ISchedule[]) =>
        data.map((content) => ({
            scheduled: new Date(content.schedule_date),
            type: content.type as postType
        }))

    const hydrateFrom = (data: ISchedule[]) => {
        setScheduledContent(data)
    }

    const fetchAndCache = async (type?: string) => {
        const requestId = ++fetchRequestId.current

        try {
            setIsLoading(true)

            const data = await getScheduledContent(type)

            // Keep the complete unfiltered response in cache.
            // Do not replace the full cache with a filtered response.
            if (!type) {
                setCache(data)
            }

            // Prevent an older/slower request from overwriting
            // the most recently selected content type.
            if (requestId === fetchRequestId.current) {
                hydrateFrom(data)
            }
        } finally {
            if (requestId === fetchRequestId.current) {
                setIsLoading(false)
            }
        }
    }

    const deleteScheduledPost = useMutation(
        async (id: string) => await deleteScheduledPostFn(id),
        {
            onSuccess: async () => {
                toast.dismiss()

                await fetchAndCache(selectedOption || undefined)
            },
            onError: () => {
                toast.dismiss()
                addToast('error', 'An error occured, please try again')
            }
        }
    )

    const postNowMutation = useMutation(
        async (id: string) => await postContentNow(id),
        {
            onSuccess: async () => {
                toast.dismiss()

                await fetchAndCache(selectedOption || undefined)
            },
            onError: () => {
                toast.dismiss()
                addToast('error', 'An error occured, please try again')
            }
        }
    )

    useEffect(() => {
        setTitle('Schedule')
    }, [setTitle])

    // Initial load:
    // show complete cached data immediately if available,
    // then refresh the complete schedule list from the backend.
    useEffect(() => {
        if (cachedScheduled) {
            hydrateFrom(cachedScheduled)

            if (cachedScheduled.length) {
                setIsLoading(false)
            }
        }

        void fetchAndCache()
    }, [])

    // Content-type changes ALWAYS query the backend.
    // Skip the initial render because the initial-load effect above
    // already fetches the complete schedule list.
    useEffect(() => {
        if (!hasMountedFilter.current) {
            hasMountedFilter.current = true
            return
        }

        void fetchAndCache(selectedOption || undefined)
    }, [selectedOption])

    useEffect(() => {
        if (location?.state?.scheduleMessages) {
            setSelectedOption('message')
            navigate('', { replace: true })
        }
    }, [location, navigate])

    const convertScheduledContentToScheduledList = (
        list: ISchedule[],
        selectedDate: Date | null
    ): IScheduledListDate[] => {
        const start = selectedDate ? new Date(selectedDate) : new Date()
        start.setHours(0, 0, 0, 0)

        const end = new Date(start)

        if (selectedDate) {
            end.setHours(23, 59, 59, 999)
        } else {
            end.setDate(end.getDate() + 7)
            end.setHours(23, 59, 59, 999)
        }

        const tempList: IScheduledListDate[] = []

        list
            .filter((content) => {
                const contentDate = new Date(content.schedule_date)

                return contentDate >= start && contentDate <= end
            })
            .forEach((content) => {
                const contentDate = new Date(content.schedule_date)

                const existingDateIndex = tempList.findIndex((element) => {
                    return (
                        element.date.dayOfMonth === contentDate.getDate() &&
                        element.date.nameOfDay === contentDate.getDay()
                    )
                })

                let contentId = content.id

                if (
                    ['post', 'premium', 'reel'].includes(content.type) &&
                    content.post_id
                ) {
                    contentId = content.post_id
                }

                if (content.type === 'story' && content.story_id) {
                    contentId = content.story_id
                }

                if (content.type === 'stream' && content.live_id) {
                    contentId = content.live_id
                }

                if (content.type === 'message' && content.message_id) {
                    contentId = content.message_id
                }

                const item = {
                    postId: contentId,
                    scheduleId: content.id,
                    postType: content.type,
                    imageUrl: content.thumb_url ?? content.url ?? null,
                    groups: content.groups ?? [],
                    scheduledDate: contentDate
                }

                if (existingDateIndex >= 0) {
                    tempList[existingDateIndex].list.push(item)
                } else {
                    tempList.push({
                        date: {
                            dayOfMonth: contentDate.getDate(),
                            nameOfDay: contentDate.getDay()
                        },
                        list: [item]
                    })
                }
            })

        return tempList
    }

    const scheduledCalendarPosts = useMemo(
        () => schedulesToCalendar(scheduledContent),
        [scheduledContent]
    )

    // No content-type filtering here.
    // scheduledContent already contains the response for the
    // currently selected backend filter.
    const scheduledList = useMemo(() => {
        return convertScheduledContentToScheduledList(scheduledContent, date)
    }, [scheduledContent, date])

    const options = [
        {
            color: '#262A33',
            value: '',
            label: t('allShares')
        },
        {
            color: '#6ABEFF',
            value: 'post',
            label: t('scheduleOptionsPosts')
        },
        {
            color: '#F7DE5B',
            value: 'premium',
            label: t('scheduleOptionsPremium')
        },
        {
            color: '#FF8A3D',
            value: 'reel',
            label: 'Reels'
        },
        {
            color: '#D27FBB',
            value: 'message',
            label: t('scheduleOptionsMessages')
        },
        {
            color: '#7FD287',
            value: 'story',
            label: t('scheduleOptionsStories')
        },
        {
            color: '#FF6F6F',
            value: 'stream',
            label: t('scheduleOptionsStreams')
        }
    ]

    const optionsArray = () => {
        switch (contentType) {
            case 'post':
                return [
                    {
                        icon: AllIcons.post_edit,
                        text: t('editScheduledPost'),
                        disabled: false,
                        action: (id: string) => {
                            navigate(`/edit/post/${contentId}`, {
                                state: {
                                    fromSchedule: true,
                                    scheduleId
                                }
                            })
                        }
                    },
                    {
                        icon: Icons.darkSend,
                        text: t('postRightNow'),
                        disabled: false,
                        action: (id: string) => {
                            handlePostNow(id)
                        }
                    }
                ]

            case 'reel':
                return [
                    {
                        icon: AllIcons.post_edit,
                        text: t('editScheduledPost'),
                        disabled: false,
                        action: (id: string) => {
                            navigate(`/edit/post/${id}`)
                        }
                    },
                    {
                        icon: Icons.darkSend,
                        text: t('postRightNow'),
                        disabled: false,
                        action: (id: string) => {
                            handlePostNow(id)
                        }
                    }
                ]

            case 'message':
                return [
                    {
                        icon: Icons.darkSend,
                        text: t('sendMessageRightNow'),
                        disabled: false,
                        action: (id: string) => {
                            handlePostNow(id)
                        }
                    }
                ]

            case 'story':
                return [
                    {
                        icon: Icons.darkSend,
                        text: t('shareStoryRightNow'),
                        disabled: false,
                        action: (id: string) => {
                            handlePostNow(id)
                        }
                    }
                ]

            case 'stream':
                return [
                    {
                        icon: AllIcons.post_edit,
                        text: t('editScheduledStream'),
                        disabled: false,
                        action: (id: string) => {
                            navigate(`/edit/post/${id}`)
                        }
                    }
                ]

            default:
                return [
                    {
                        icon: AllIcons.post_edit,
                        text: t('editScheduledPost'),
                        disabled: false,
                        action: (id: string) => {
                            navigate(`/edit/post/${id}`)
                        }
                    },
                    {
                        icon: Icons.darkSend,
                        text: t('postRightNow'),
                        disabled: false,
                        action: (id: string) => {
                            handlePostNow(id)
                        }
                    }
                ]
        }
    }

    const handlePostNow = (id: string) => {
        postNowMutation.mutate(id)
        setIsOptionsNavOpen(false)
    }

    const openScheduleContentModal = () => {
        modalData.addModal(
            'Schedule',
            <ScheduleContent date={date ?? new Date()} />,
            false
        )
    }

    return (
        <div className="top-div-schedule">
            <WithHeaderSection
                customClass="whs-schedule"
                headerSection={
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            width: '100%',
                            gap: 12,
                            fontSize: 12
                        }}
                    >
                        <div
                            className={styles.dropdownWrapper}
                            style={{
                                flex: 1,
                                minWidth: 0,
                                display: 'flex'
                            }}
                        >
                            <DropDownSelect
                                icon={
                                    <div className="analytics-dropdown-icon">
                                        <IconShareCircles />
                                    </div>
                                }
                                placeholder={t('allShares')}
                                options={options}
                                search={search}
                                setSearch={setSearch}
                                selectedOption={selectedOption}
                                setSelectedOption={setSelectedOption}
                                notSearchable
                            />
                        </div>

                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8
                            }}
                        >
                            <IconButton
                                icon={
                                    <IconCalendarLinesWhite
                                        color={
                                            showBigCalendar
                                                ? 'white'
                                                : '#262a33'
                                        }
                                        width={'20'}
                                        height={'20'}
                                    />
                                }
                                type={showBigCalendar ? 'black' : 'white'}
                                clickFn={() =>
                                    setShowBigCalendar(
                                        (prevValue) => !prevValue
                                    )
                                }
                            />

                            <IconButton
                                icon={<IconPlus />}
                                type={'white'}
                                clickFn={() => openScheduleContentModal()}
                            />
                        </div>
                    </div>
                }
            >
                <div className="calendar-wrapper">
                    {showBigCalendar && (
                        <>
                            <div
                                className="calendar-box"
                                style={{
                                    minHeight: 340,
                                    background: '#fff',
                                    borderRadius: 16,
                                    transition: 'min-height 0.2s',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    width: '100%'
                                }}
                            >
                                <div
                                    className={styles.scheduleCalendarWrapper}
                                    style={{
                                        opacity: isLoading ? 0.75 : 1,
                                        transition: 'opacity 0.2s ease'
                                    }}
                                >
                                    <InlineDatePicker
                                        onChange={(value) =>
                                            setDate(value as Date)
                                        }
                                        value={date ?? undefined}
                                        scheduledPosts={scheduledCalendarPosts}
                                        dotsColor=""
                                    />
                                </div>
                            </div>

                            <div className={styles.separator}></div>
                        </>
                    )}
                </div>

                <div className={styles.list_container}>
                    {isLoading ? (
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                padding: 20
                            }}
                        >
                            <BeatLoader size={8} />
                        </div>
                    ) : scheduledList && scheduledList.length > 0 ? (
                        <ScheduleList
                            onEditClick={(
                                postType,
                                postId,
                                scheduleId
                            ) => {
                                setContentId(postId)
                                setScheduleId(scheduleId)
                                setContentType(postType)
                                setIsOptionsNavOpen(true)
                            }}
                            list={scheduledList}
                            date={date}
                        />
                    ) : (
                        <p className={styles['no-scheduled-posts']}>
                            No Scheduled Posts
                        </p>
                    )}
                </div>
            </WithHeaderSection>

            {/* 
            <DesktopAdditionalContent>
                {scheduledList && scheduledList.length > 0 ? (
                    <ScheduleList
                        onEditClick={(postType, postId) => {
                            setContentId(postId)
                            setContentType(postType)
                            setIsOptionsNavOpen(true)
                        }}
                        list={scheduledList}
                        date={date}
                    />
                ) : (
                    <p className={styles['no-scheduled-posts']}>
                        No Scheduled Posts
                    </p>
                )}
            </DesktopAdditionalContent>
            */}

            {isOptionsNavOpen && (
                <OptionsDropdown
                    role="owner"
                    content={contentType}
                    isOptionsNavOpen={isOptionsNavOpen}
                    setIsOptionsNavOpen={setIsOptionsNavOpen}
                    postId={contentId}
                    options={optionsArray()}
                    deleteFn={() => {
                        deleteScheduledPost.mutate(scheduleId)
                    }}
                />
            )}
        </div>
    )
}
